import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { fadeInUp } from '../utils/cn';

const TelecommunicationDivisionCompany = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="section-padding solid-bg text-white relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('/divisions/Telecommunications/telecommunication-equipment-for-5g-radio-network-2024-12-03-04-12-51-utc.jpg')`
          }}
        />
        
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-accent rounded-full opacity-30 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="text-center"
            {...fadeInUp}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Our Partner Companies
            </h1>
            <p className="text-xl md:text-xl mb-8 leading-relaxed max-w-4xl mx-auto">
              Partnering with industry leaders to deliver world-class telecommunication solutions. 
              We collaborate with specialized companies that share our commitment to safety, 
              innovation, and excellence in telecommunication environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="secondary">
                  Get Quote
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
              <Link to="/divisions/telecommunication">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                  Back to Telecommunication Division
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="section-padding bg-white text-black">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-global-green">
              Companies Global Technology Represent
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Partnering with industry leaders to deliver world-class telecommunication solutions.
            </p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-r from-charcoal to-slate rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4 flex items-center text-global-green">
                  <Shield className="w-8 h-8 mr-3 text-global-green" />
                  Guardian Telecom Ltd.
                </h3>
                <h4 className="text-xl font-semibold mb-4 text-global-green">
                  Rugged, safe, reliable and cost-effective 
                  telecommunication solutions for industries 
                  worldwide.
                </h4>
                <p className="text-black mb-6 leading-relaxed">
                  Guardian Telecom designs, builds, and commissions 
                  Telephones, PA/GA, Page/Talk, and Talk/Back paging 
                  systems. Each product is fully integrated “ruggedized” 
                  high performance telecommunications equipment 
                  for non-hazardous, hazardous, Class I Division 1, 
                  Class I Division 2 and other hardened equipment 
                  applications. Guardian Telecom products are 
                  engineered from the ground up to work reliably and 
                  last – especially in the roughest weather, toughest 
                  applications and most extreme environments.
                </p>
                <p className="text-black mb-6 leading-relaxed">
                  You will find Guardian Telecom products in 
                  power plants in the hot desert sun to production 
                  platforms battling arctic temperatures, and high 
                  vibrations within manufacturing plants, or extreme 
                  environmental conditions in nuclear facilities, thermal 
                  and hydro plants, and into mining excavations.
                  Guardian Telecom equipment and systems are 
                  manufactured for transportation facilities, tunnels, 
                  highways, rail systems, airports, and on travel cruise 
                  liners. Our products are placed in pharmaceutical 
                  plants, grain, and food processing plants.
                  Top military units acquire Guardian Telecom 
                  ruggedized equipment to be used in ships, buildings, 
                  tankers and war zones. Space agencies use Guardian 
                  Telecom equipment throughout their work 
                  environments.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center text-black">
                    <Shield className="w-5 h-5 text-global-green mr-2" />
                    <span className="text-sm">80+ Years Experience</span>
                  </div>
                  <div className="flex items-center text-black">
                    <Shield className="w-5 h-5 text-global-green mr-2" />
                    <span className="text-sm">ISO standards</span>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Product Range</h4>
                    <ul className="space-y-2 text-black">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Telephones for hazardous and non-hazardous areas
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        PA/GA (Public Address/General Alarm) systems
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Page/Talk and Talk/Back paging systems
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Class I Division 1 & 2 certified equipment
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Applications</h4>
                    <ul className="space-y-2 text-black">
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Power plants and production platforms
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                       Manufacturing plants and mining excavations
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Nuclear, thermal, and hydro facilities
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Transportation and military applications
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="border-t border-black/20 pt-6">
                  <p className="text-sm leading-relaxed opacity-90 text-black">
                    Guardian Telecom designs, builds, and commissions fully integrated "ruggedized" high performance telecommunications equipment for extreme environments. Each product is engineered from the ground up to work reliably and last—especially in the roughest weather, toughest applications and most extreme environments.
                  </p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-lg font-semibold mb-2 text-black">Industry Markets</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Oil & Gas</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Power Generation</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Mining</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Transportation</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Military</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Space Agencies</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Pharmaceuticals</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Food Processing</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Nuclear Facilities</span>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <p className="text-sm font-medium text-black">
                   Guardian Telecom stands behind its products with the industry's best warranty and support
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TelecommunicationDivisionCompany;
